import { useRef, useState } from "react";
import { DownloadSimple, FileCsv, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Card, inputCls, labelCls, btnPrimary, ReadOnlyNote } from "./shared";

const FIELDS = [["", "— Ignora —"], ["first_name", "Nome"], ["last_name", "Cognome"], ["phone", "Telefono"],
  ["email", "Email"], ["birth_date", "Data di nascita"], ["city", "Città"], ["notes", "Note"],
  ["tags", "Tag"], ["marketing_consent", "Consenso marketing"], ["zip", "CAP"], ["province", "Provincia"],
  ["country", "Nazione"], ["visits_imported", "Visite storiche"], ["total_spent", "Spesa totale storica (€)"]];

export default function ImportazioneSection({ readOnly }) {
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [skipDup, setSkipDup] = useState(true);
  const [updateDup, setUpdateDup] = useState(false);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const downloadTemplate = async () => {
    const r = await api.get("/customers/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "template_clienti_dishenda.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => doPreview(reader.result, null);
    reader.readAsText(file);
  };

  const doPreview = async (text, map) => {
    setBusy(true);
    try {
      const r = await api.post("/customers/import/preview", { csv_content: text, mapping: map });
      setCsvText(text);
      setPreview(r.data);
      setMapping(r.data.mapping);
      setStep(2);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/customers/import/confirm",
        { csv_content: csvText, mapping, skip_duplicates: skipDup, update_existing: updateDup });
      setReport(r.data);
      setStep(3);
      const n = r.data.imported;
      toast.success(`Importazione completata: ${n} ${n === 1 ? "cliente importato" : "clienti importati"}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="setsection-import">
      {readOnly ? <ReadOnlyNote /> : null}
      {!readOnly && step === 1 && (
        <Card title="Importa clienti da CSV"
          desc="Per chi arriva da un altro gestionale: scarica il template, compilalo e caricalo. Max 2MB / 5000 righe. Duplicati rilevati su telefono ed email."
          testid="import-step1">
          <div className="flex flex-wrap gap-3">
            <button onClick={downloadTemplate} className="h-11 px-4 rounded-md border border-zinc-300 text-sm font-semibold flex items-center gap-2"
              data-testid="import-template-btn"><DownloadSimple size={16} /> Scarica template CSV</button>
            <button onClick={() => fileRef.current.click()} disabled={busy}
              className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="import-upload-btn"><UploadSimple size={16} /> {busy ? "Analisi..." : "Carica CSV"}</button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => onFile(e.target.files[0])} data-testid="import-file" />
          </div>
          <p className="text-xs text-zinc-500 flex items-center gap-1.5"><FileCsv size={14} /> Separatore virgola o punto e virgola, rilevato automaticamente.</p>
        </Card>
      )}

      {!readOnly && step === 2 && preview && (
        <>
          <Card title="Mappatura dei campi" desc="Associa le colonne del tuo CSV ai campi Dishenda." testid="import-mapping">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {preview.headers.map((hcol) => (
                <div key={hcol}>
                  <label className={labelCls}>{hcol}</label>
                  <select value={mapping[hcol] || ""}
                    onChange={(e) => { const m = { ...mapping, [hcol]: e.target.value }; setMapping(m); doPreview(csvText, m); }}
                    className={inputCls} data-testid={`import-map-${hcol}`}>
                    {FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </Card>
          <Card title={`Anteprima: ${preview.total} righe — ${preview.valid} valide, ${preview.with_errors} con errori, ${preview.duplicates} duplicate`}
            testid="import-preview">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Nome</th><th className="py-2 pr-3">Telefono</th>
                    <th className="py-2 pr-3">Email</th><th className="py-2">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.index} className="border-b border-zinc-100" data-testid={`import-row-${r.index}`}>
                      <td className="py-2 pr-3 text-zinc-400">{r.index + 1}</td>
                      <td className="py-2 pr-3 font-medium text-zinc-900">{r.data.first_name} {r.data.last_name}</td>
                      <td className="py-2 pr-3">{r.data.phone}</td>
                      <td className="py-2 pr-3">{r.data.email}</td>
                      <td className="py-2">
                        {r.errors.length > 0
                          ? <span className="text-rose-600 font-semibold">{r.errors.join(", ")}</span>
                          : r.duplicate
                            ? <span className="text-amber-600 font-semibold">Duplicato</span>
                            : <span className="text-emerald-600 font-semibold">OK</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700">
              <input type="checkbox" checked={updateDup} onChange={(e) => setUpdateDup(e.target.checked)}
                className="w-4 h-4 accent-zinc-900" data-testid="import-update-dup" /> Aggiorna i clienti già presenti (aggiunge tag, visite e dati mancanti — ideale per re-importare un export aggiornato)
            </label>
            {!updateDup && (
            <label className="flex items-center gap-2 text-sm text-zinc-700">
              <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)}
                className="w-4 h-4 accent-zinc-900" data-testid="import-skip-dup" /> Salta i duplicati (telefono/email già presenti)
            </label>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setStep(1); setPreview(null); }}
                className="h-11 px-4 rounded-md border border-zinc-300 text-sm font-semibold" data-testid="import-back">Indietro</button>
              <button onClick={confirm} disabled={busy} className={btnPrimary} data-testid="import-confirm">
                {busy ? "Importazione..." : `Conferma importazione (${preview.valid - (!updateDup && skipDup ? preview.duplicates : 0)} clienti)`}
              </button>
            </div>
          </Card>
        </>
      )}

      {!readOnly && step === 3 && report && (
        <Card title="Report importazione" testid="import-report">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="bg-emerald-50 rounded-lg p-4">
              <div className="text-2xl font-black text-emerald-700" data-testid="import-report-ok">{report.imported}</div>
              <div className="text-xs text-emerald-700">Importati</div>
            </div>
            <div className="bg-sky-50 rounded-lg p-4">
              <div className="text-2xl font-black text-sky-700" data-testid="import-report-upd">{report.updated || 0}</div>
              <div className="text-xs text-sky-700">Aggiornati</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="text-2xl font-black text-amber-700" data-testid="import-report-dup">{report.skipped_duplicates}</div>
              <div className="text-xs text-amber-700">Duplicati saltati</div>
            </div>
            <div className="bg-rose-50 rounded-lg p-4">
              <div className="text-2xl font-black text-rose-700" data-testid="import-report-err">{report.errors}</div>
              <div className="text-xs text-rose-700">Errori</div>
            </div>
          </div>
          {report.error_rows?.length > 0 && (
            <p className="text-xs text-rose-600">Righe con errori: {report.error_rows.map((e) => e.index + 1).join(", ")}</p>
          )}
          <button onClick={() => { setStep(1); setPreview(null); setReport(null); }}
            className={btnPrimary} data-testid="import-new">Nuova importazione</button>
        </Card>
      )}
    </div>
  );
}
